<template>
  <div class="game-manager">
    <div class="game-controls">
      <h3>Game Management</h3>

      <div class="current-status">
        <span class="status-label">Party State:</span>
        <span class="status-value">{{ currentView || 'lobby' }}</span>
        <span v-if="activeGameIndex !== null" class="active-game">
          Playing Game {{ activeGameIndex + 1 }}
        </span>
      </div>

      <!-- Real-time Scoring Option -->
      <div class="scoring-options">
        <label class="checkbox-label">
          <input
            type="checkbox"
            :checked="realTimeScoringEnabled"
            @change="onRealTimeScoringToggle"
          />
          <span class="checkmark"></span>
          Real-time scoring updates
          <small class="help-text">Automatically refresh scores when players answer</small>
        </label>

        <label class="checkbox-label">
          <input
            type="checkbox"
            :checked="autoRevealEnabled"
            @change="onAutoRevealToggle"
          />
          <span class="checkmark"></span>
          Auto-reveal answers
          <small class="help-text">Automatically reveal correct answers when all players have completed the question</small>
        </label>
      </div>

      <!-- Game List -->
      <div class="games-section">
        <h4>Available Games ({{ availableGames.length }})</h4>

        <div v-if="availableGames.length === 0" class="no-games">
          <p>No games configured for this party.</p>
          <button @click="goBackToParty" class="nav-button">← Back to Party Setup</button>
        </div>

        <div v-else class="games-list">
          <div
            v-for="(game, index) in availableGames"
            :key="`game-${index}`"
            class="game-item"
            :class="{
              'active': activeGameIndex === index,
              'completed': completedGames.includes(index),
              'disabled': activeGameIndex !== null && activeGameIndex !== index
            }"
          >
            <div class="game-info">
              <div class="game-header">
                <h5 class="game-title">{{ game.name || `Game ${index + 1}` }}</h5>
                <span class="game-type">{{ game.gameplaySlug || 'unknown' }}</span>
              </div>
              <div class="game-details">
                <span class="screen-count">{{ getScreenCount(game) }} screens</span>
                <span v-if="game.config?.description" class="game-description">{{ game.config.description }}</span>
              </div>
            </div>

            <div class="game-actions">
              <button
                v-if="activeGameIndex !== index"
                @click="startSpecificGame(index, game)"
                :disabled="activeGameIndex !== null || !canStartGames"
                class="action-button start-button"
              >
                <i class="bi bi-play-fill me-1"></i>
                {{ completedGames.includes(index) ? 'Replay' : 'Start' }}
              </button>

              <button
                v-if="activeGameIndex === index"
                @click="endCurrentGame(index)"
                class="action-button end-button"
              >
                <i class="bi bi-stop-fill me-1"></i>
                End Game
              </button>

              <!-- Screen Controls for Multi-Screen Games (Quiz & Drag-Match) -->
              <div v-if="activeGameIndex === index && isMultiScreenGame(game)" class="screen-controls">
                <button
                  @click="previousScreen()"
                  :disabled="currentScreenIndex <= 0"
                  class="btn btn-dark btn-sm"
                  style="min-width: 8rem;"
                  title="Previous Screen"
                >
                  <i class="bi bi-chevron-left"></i>
                  Previous
                </button>

                <span class="screen-indicator">
                  {{ (currentScreenIndex || 0) + 1 }} / {{ getScreenCount(game) }}
                </span>

                <button
                  @click="nextScreen()"
                  :disabled="(currentScreenIndex || 0) >= getScreenCount(game) - 1"
                  class="btn btn-dark btn-sm"
                  style="min-width: 8rem;"
                  title="Next Screen"
                >
                  Next
                  <i class="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Global Navigation -->
      <div class="global-navigation">
        <button
          @click="changeView('lobby')"
          :disabled="currentView === 'lobby'"
          class="nav-button"
        >
          <i class="bi bi-house me-1"></i>
          Lobby
        </button>

        <button
          @click="changeView('score')"
          :disabled="!hasScores"
          class="nav-button"
        >
          <i class="bi bi-trophy me-1"></i>
          Show Scores
        </button>

        <button
          @click="changeView('end')"
          class="nav-button"
        >
          <i class="bi bi-flag-checkered me-1"></i>
          End Party
        </button>

        <button
          @click="goBackToParty"
          class="nav-button back-button"
        >
          <i class="bi bi-arrow-left me-1"></i>
          Back to Setup
        </button>
      </div>
    </div>

    <div class="player-status">
      <h4>Players ({{ playerCount }})</h4>
      <table class="player-table">
        <tr>
          <th>Avatar</th>
          <th>Name</th>
          <th>Correct</th>
          <th>Fastest</th>
          <th>Score</th>
          <th>Completed</th>
        </tr>
        <tr
          v-for="player in playersArray"
          :key="player.playerId"
          class="player-item"
          :class="{ 'ready': player.isReady, 'loading': player.isLoading }"
        >
          <td class="player-avatar">{{ player.avatar }}</td>
          <td class="player-name">
            <div class="player-real-name" v-if="player.firstName">{{ player.firstName }}</div>
            <div class="player-pseudo" :class="{ 'with-real-name': player.firstName }">{{ player.playerName }}</div>
          </td>
          <td class="player-correct">
            {{ player.correctAnswers || 0 }}
          </td>
          <td class="player-fastest">
            {{ player.fastestAnswers || 0 }}
          </td>
          <td class="player-score">
            {{ player.score || 0 }} pts
          </td>
          <td class="player-completed">
            <i class="bi bi-check-circle-fill" v-if="player.completedQuestions > 0"></i>
          </td>
        </tr>
      </table>
    </div>

    <!-- Active Game Progress -->
    <div class="game-progress" v-if="activeGameIndex !== null">
      <h4>Game Progress</h4>
      <div class="progress-info">
        <div class="current-question" v-if="currentQuestion">
          <strong>Current Screen:</strong> {{ currentQuestion.text || 'Screen ' + ((currentScreenIndex || 0) + 1) }}
        </div>
        <div class="answer-stats" v-if="answerStats">
          <span>Answers received: {{ answerStats.received }}/{{ answerStats.total }}</span>
        </div>
      </div>
    </div>

    <!-- Active Game Interface -->
    <div class="game-interface" v-if="activeGameIndex !== null && activeGameData">
      <div class="interface-header">
        <h4><i class="bi bi-controller"></i> Game Interface</h4>
        <small class="text-muted">Detailed game controls and monitoring</small>
      </div>
      <HostGameLoader
        :game-data="activeGameData"
        :connected-players="connectedPlayersArray"
        :signaling="signaling"
        :host-signaling="hostSignaling"
        :current-screen-index="currentScreenIndex"
        :active-game-index="activeGameIndex"
        @game-started="handleGameLoaderStarted"
        @game-ended="handleGameLoaderEnded"
        @screen-changed="handleGameLoaderScreenChanged"
        @answers-revealed="handleGameLoaderAnswersRevealed"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import HostGameLoader from './games/HostGameLoader.vue'

// Props
interface Props {
  currentView?: string
  currentScreenIndex?: number
  activeGameIndex?: number | null
  players?: Array<{
    playerId: string
    playerName: string
    avatar: string
    isReady: boolean
    isLoading: boolean
    score?: number
    correctAnswers?: number,
    fastestAnswers?: number,
  }>
  currentQuestion?: {
    text: string
    options: string[]
  } | null
  answerStats?: {
    received: number
    total: number
  } | null
  partyData?: any
  connectedPlayers?: Map<string, any>
  signaling?: any
  completedGames?: number[]
  hostSignaling?: {
    startGame: (gameIndex: number, gameType: string, gameData: any) => void
    endGame: (gameIndex: number) => void
    changeView: (view: string) => void
    nextScreen: () => void
    previousScreen: () => void
    displayScoreUpdate: (updates: any) => void
    realTimeScoring?: boolean
    autoReveal?: boolean
    revealAnswers?: () => void
  }
}

const props = withDefaults(defineProps<Props>(), {
  currentView: 'lobby',
  currentScreenIndex: 0,
  activeGameIndex: null,
  players: () => [],
  currentQuestion: null,
  answerStats: null,
  partyData: () => null,
  connectedPlayers: () => new Map(),
  signaling: () => null,
  completedGames: () => [],
  hostSignaling: () => ({
    startGame: () => {},
    endGame: () => {},
    changeView: () => {},
    nextScreen: () => {},
    previousScreen: () => {},
    displayScoreUpdate: () => {}
  })
})

// Emits
interface Emits {
  (e: 'change-view', view: string): void
  (e: 'start-game', gameIndex: number, gameData: any): void
  (e: 'end-game', gameIndex: number): void
  (e: 'next-screen'): void
  (e: 'previous-screen'): void
  (e: 'back-to-party'): void
  (e: 'update-real-time-scoring', enabled: boolean): void
  (e: 'update-auto-reveal', enabled: boolean): void
}

const emit = defineEmits<Emits>()

// Computed
const playerCount = computed(() => {
  if (props.connectedPlayers) {
    return props.connectedPlayers.size
  }
  return props.players.length
})

const playersArray = computed(() => {
  if (props.connectedPlayers) {
    return Array.from(props.connectedPlayers.values()).map(player => ({
      playerId: player.playerId || player.id,
      playerName: player.playerName || player.name,
      firstName: player.firstName || '',
      pseudo: player.pseudo || '',
      avatar: player.avatar || '😊',
      isReady: player.isReady || false,
      isLoading: player.isLoading || false,
      score: player.score || 0,
      correctAnswers: player.correctAnswers || 0,
      fastestAnswers: player.fastestAnswers || 0,
      completedQuestions: player.completedQuestions || 0
    }))
  }
  // Fallback to props.players but ensure consistent structure
  return props.players.map(player => ({
    ...player,
    firstName: '', // Not available in props.players fallback
    pseudo: '',    // Not available in props.players fallback
    completedQuestions: 0 // Not available in props.players fallback
  }))
})

const availableGames = computed(() => {
  return props.partyData?.games || []
})

const canStartGames = computed(() => {
  return playersArray.value.length > 0 &&
         playersArray.value.every(p => p.isReady && !p.isLoading)
})

const hasScores = computed(() => {
  return playersArray.value.some(p => p.score !== undefined && p.score > 0)
})

const completedGames = computed(() => {
  return props.completedGames || []
})

const activeGameIndex = computed(() => {
  return props.activeGameIndex
})

const currentScreenIndex = computed(() => {
  return props.currentScreenIndex || 0
})

const realTimeScoringEnabled = computed(() => {
  const value = props.hostSignaling?.realTimeScoring || false
  console.log('🔍 realTimeScoringEnabled computed:', value)
  return value
})

const autoRevealEnabled = computed(() => {
  const value = props.hostSignaling?.autoReveal ?? true // Default to true
  return value
})

const activeGameData = computed(() => {
  if (props.activeGameIndex !== null && availableGames.value[props.activeGameIndex]) {
    return availableGames.value[props.activeGameIndex]
  }
  return null
})

const connectedPlayersArray = computed(() => {
  return playersArray.value
})

// Methods
function getScreenCount(game: any): number {
  return game.screens?.length || 0
}

function isQuizGame(game: any): boolean {
  return game.gameplaySlug === 'basic-quiz'
}

function isMultiScreenGame(game: any): boolean {
  return true
  return game.gameplaySlug === 'basic-quiz' || game.gameplaySlug === 'drag-and-match'
}

function onRealTimeScoringToggle(event: Event) {
  const target = event.target as HTMLInputElement
  const newValue = target.checked

  console.log('🔄 Checkbox toggled to:', newValue)
  console.log('🔍 Current hostSignaling object:', props.hostSignaling)
  console.log('🔍 Current realTimeScoring value:', props.hostSignaling?.realTimeScoring)

  // Since the ref gets unwrapped when passed as prop, we need to emit an event
  // to update the parent's ref instead of trying to modify the prop directly
  emit('update-real-time-scoring', newValue)
}

function onAutoRevealToggle(event: Event) {
  const target = event.target as HTMLInputElement
  const newValue = target.checked

  console.log('🔄 Auto-reveal toggled to:', newValue)

  // Emit event to update parent's ref
  emit('update-auto-reveal', newValue)
}

// Auto-reveal logic - checks if all players completed current screen/question
function checkAndAutoReveal() {
  console.log('🎯 checkAndAutoReveal called')
  console.log('🎯 autoRevealEnabled:', autoRevealEnabled.value)
  console.log('🎯 activeGameIndex:', props.activeGameIndex)

  if (!autoRevealEnabled.value || props.activeGameIndex === null) {
    console.log('🎯 Auto-reveal skipped: enabled =', autoRevealEnabled.value, 'activeGame =', props.activeGameIndex)
    return
  }

  const totalPlayers = playersArray.value.length
  console.log('🎯 Total players:', totalPlayers)

  if (totalPlayers === 0) {
    console.log('🎯 No players, skipping auto-reveal')
    return
  }

  // ONLY use answer stats for auto-reveal - no fallback mode to prevent premature reveals
  if (props.answerStats) {
    const { received, total } = props.answerStats
    console.log(`🎯 Answer stats: ${received}/${total} answers received`)

    // Auto-reveal when all expected players have submitted complete answers
    if (received >= total && received > 0) {
      console.log('🎉 All players have submitted complete answers! Auto-revealing...')
      if (props.hostSignaling?.revealAnswers) {
        props.hostSignaling.revealAnswers()
      }
    } else {
      console.log(`🎯 Not all players have completed the current screen yet: ${received}/${total}`)
    }
  } else {
    console.log('🎯 No answer stats available - auto-reveal disabled to prevent premature reveals')
    console.log('🎯 Answer stats are required for reliable completion tracking')
  }
}

// Expose checkAndAutoReveal so parent can call it when receiving player answers
defineExpose({
  checkAndAutoReveal
})

function startSpecificGame(gameIndex: number, game: any) {
  // Only emit the event, let the parent handle the actual signaling to avoid duplicates
  emit('start-game', gameIndex, game)
}

function endCurrentGame(gameIndex: number) {
  // Only emit the event, let the parent handle the actual signaling to avoid duplicates
  emit('end-game', gameIndex)
}

function nextScreen() {
  if (props.hostSignaling) {
    props.hostSignaling.nextScreen()
  }
  emit('next-screen')
}

function previousScreen() {
  if (props.hostSignaling) {
    props.hostSignaling.previousScreen()
  }
  emit('previous-screen')
}

function changeView(view: string) {
  if (props.hostSignaling) {
    props.hostSignaling.changeView(view)
  }
  emit('change-view', view)
}

function goBackToParty() {
  emit('back-to-party')
}

// HostGameLoader event handlers
function handleGameLoaderStarted(gameType: string, config: any) {
  console.log('GameLoader: Game started', gameType, config)
}

function handleGameLoaderEnded(gameType: string) {
  console.log('GameLoader: Game ended', gameType)
}

function handleGameLoaderScreenChanged(screenIndex: number) {
  console.log('GameLoader: Screen changed to', screenIndex)
  // Screen changes are handled by the individual game interfaces
  // and synchronized through the host signaling system
}

function handleGameLoaderAnswersRevealed(screenIndex: number, correctAnswers: any) {
  console.log('GameLoader: Answers revealed for screen', screenIndex, correctAnswers)
  // This is handled by the HostGameLoader itself
}

function getPlayerStateText(player: any) {
  if (player.isLoading) return 'Loading...'
  if (player.isReady) return 'Ready'
  return 'Not Ready'
}
</script>

<style scoped>
.game-manager {
  padding: 1rem;
  border-radius: 8px;
  background: #f5f5f5;
  margin-bottom: 1rem;
  width: 100%;
  max-width: 800px;
}

.game-controls {
  margin-bottom: 1.5rem;
}

.current-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.status-label {
  font-weight: bold;
}

.status-value {
  padding: 0.25rem 0.5rem;
  background: #007acc;
  color: white;
  border-radius: 4px;
  text-transform: capitalize;
}

.active-game {
  padding: 0.25rem 0.5rem;
  background: #ff6b35;
  color: white;
  border-radius: 4px;
  font-size: 0.875rem;
}

.games-section {
  margin-bottom: 1.5rem;
}

.games-section h4 {
  margin: 0 0 0.75rem 0;
  color: #333;
}

.no-games {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.games-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.game-item {
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
}

.game-item:hover:not(.disabled) {
  border-color: #007acc;
  box-shadow: 0 2px 4px rgba(0, 122, 204, 0.1);
}

.game-item.active {
  border-color: #ff6b35;
  background: #fff5f0;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.2);
}

.game-item.completed {
  border-color: #28a745;
  background: #f8fff8;
}

.game-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.game-info {
  flex: 1;
  margin-bottom: 0.75rem;
}

.game-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.game-title {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.game-type {
  font-size: 0.875rem;
  background: #e9ecef;
  color: #495057;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
}

.game-details {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.screen-count {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
}

.game-description {
  font-size: 0.875rem;
  color: #666;
  font-style: italic;
}

.game-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.start-button {
  background: #28a745;
  color: white;
}

.start-button:hover:not(:disabled) {
  background: #218838;
}

.end-button {
  background: #dc3545;
  color: white;
}

.end-button:hover:not(:disabled) {
  background: #c82333;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.screen-controls {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.screen-button {
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.screen-button:hover:not(:disabled) {
  background: #007acc;
  color: white;
  border-color: #007acc;
}

.screen-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.screen-indicator {
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
  min-width: 60px;
  text-align: center;
}

.global-navigation {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding-top: 1rem;
  border-top: 1px solid #ddd;
}

.nav-button {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.nav-button:hover:not(:disabled) {
  background: #007acc;
  color: white;
  border-color: #007acc;
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.back-button {
  background: #6c757d;
  border-color: #6c757d;
  color: white;
}

.back-button:hover:not(:disabled) {
  background: #545b62;
  border-color: #545b62;
}

.player-status {
  margin-bottom: 1.5rem;
}

.player-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  background-color: white;
}

.player-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.player-item {
  padding: 0.5rem;
  border-bottom: 1px solid #eee;
}

.player-item:last-child {
  border-bottom: none;
}

.player-item.ready {
  background: #f8f9fa;
}

.player-item.loading {
  background: #fff3cd;
}

.player-name {
  flex: 1;
}

.player-real-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.player-pseudo {
  color: #6c757d;
  font-size: 0.8rem;
  font-style: italic;
}

.player-pseudo.with-real-name {
  margin-top: 2px;
}

.player-avatar {
  font-size: 1.2rem;
  min-width: 28px;
  text-align: center;
}

.player-name {
  flex: 1;
  font-weight: 500;
}

.player-state {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
}

.player-score {
  font-size: 0.875rem;
  font-weight: bold;
  color: #007acc;
  min-width: 60px;
  text-align: center;
}

.player-completed {
  text-align: center;
  min-width: 80px;
}

.player-completed i {
  color: #28a745;
  margin-right: 0.25rem;
  font-size: 1rem;
}

.completed-count {
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
}

.game-progress {
  border-top: 1px solid #ddd;
  padding-top: 1rem;
}

.progress-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.current-question {
  padding: 0.5rem;
  background: #e3f2fd;
  border-radius: 4px;
  border-left: 4px solid #2196f3;
}

.answer-stats {
  font-weight: 500;
  color: #666;
}

/* Scoring Options */
.scoring-options {
  margin: 1rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
  font-size: 0.9rem;
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
  transform: scale(1.2);
}

.checkbox-label .help-text {
  display: block;
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  line-height: 1.3;
}

.checkbox-label:hover {
  background: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  padding: 0.25rem;
  margin: -0.25rem;
}

/* Game Interface Section */
.game-interface {
  margin-top: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.interface-header {
  margin-bottom: 1rem;
}

.interface-header h4 {
  margin: 0;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.interface-header small {
  display: block;
  margin-top: 0.25rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .game-interface {
    margin-top: 1rem;
    padding: 1rem;
  }
}
</style>
